import CardMenu from "components/card/CardMenu";
import React, { useState, useEffect } from "react";
import Checkbox from "components/checkbox";
import { MdDragIndicator, MdCheckCircle, MdTrendingUp, MdPeople, MdVerified } from "react-icons/md";
import { FaTrophy, FaStar, FaMedal } from "react-icons/fa";
import Card from "components/card";
import axios from 'axios';
import { Loader2, AlertCircle } from 'lucide-react';

const TaskCard = () => {
  const [topCompliance, setTopCompliance] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const api = axios.create({
    baseURL: process.env.REACT_APP_API_URL || 'http://localhost:8000/api',
    headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json'
    }
  });

  // Add token interceptor
  api.interceptors.request.use((config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });

  const GetTopCompliance = async () => {
    setLoading(true);
    setError("");
    
    try {
      const response = await api.get('/topcompliance');
      console.log('Top compliance response:', response.data);
      
      if (response.data.success) {
        setTopCompliance(response.data.data || response.data.topcompliance || []);
      } else {
        setError(response.data.message || 'Failed to fetch data');
        setTopCompliance([]);
      }
    } catch (error) {
      console.error('Error fetching top compliance:', error);
      setError(error.response?.data?.message || 'Network error occurred');
      setTopCompliance([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    GetTopCompliance();
  }, []);

  // Get rank icon based on index
  const getRankIcon = (index) => {
    switch(index) {
      case 0:
        return <FaTrophy className="h-5 w-5 text-yellow-500" />;
      case 1:
        return <FaMedal className="h-5 w-5 text-gray-400" />;
      case 2:
        return <FaMedal className="h-5 w-5 text-amber-600" />;
      default:
        return <MdVerified className="h-5 w-5 text-indigo-400" />;
    }
  };

  // Get rank badge color
  const getRankBadge = (index) => {
    switch(index) {
      case 0:
        return "bg-yellow-100 text-yellow-800";
      case 1:
        return "bg-gray-100 text-gray-800";
      case 2:
        return "bg-amber-100 text-amber-800";
      default:
        return "bg-indigo-100 text-indigo-800";
    }
  };

  // Get compliance rate color
  const getComplianceColor = (rate) => {
    if (rate >= 90) return "text-green-600";
    if (rate >= 75) return "text-blue-600";
    if (rate >= 60) return "text-yellow-600";
    return "text-red-600";
  };

  // Get compliance badge color
  const getComplianceBadge = (rate) => {
    if (rate >= 90) return "bg-green-100 text-green-800";
    if (rate >= 75) return "bg-blue-100 text-blue-800";
    if (rate >= 60) return "bg-yellow-100 text-yellow-800";
    return "bg-red-100 text-red-800";
  };

  if (loading) {
    return (
      <Card extra="pb-7 p-[20px]">
        <div className="flex flex-col items-center justify-center py-8">
          <Loader2 className="h-8 w-8 text-indigo-600 animate-spin mb-3" />
          <p className="text-gray-500 text-sm">Loading top compliant traders...</p>
        </div>
      </Card>
    );
  }

  if (error) {
    return (
      <Card extra="pb-7 p-[20px]">
        <div className="flex flex-col items-center justify-center py-8">
          <AlertCircle className="h-12 w-12 text-red-500 mb-3" />
          <p className="text-red-600 text-sm font-medium mb-2">Error loading data</p>
          <p className="text-gray-500 text-xs">{error}</p>
          <button 
            onClick={GetTopCompliance}
            className="mt-4 px-4 py-2 bg-indigo-600 text-white text-sm rounded-lg hover:bg-indigo-700 transition"
          >
            Try Again
          </button>
        </div>
      </Card>
    );
  }

  return (
    <Card extra="pb-7 p-[20px]">
      {/* Header */}
      <div className="relative flex flex-row justify-between">
        <div className="flex items-center">
          <div className="flex h-9 w-9 items-center justify-center rounded-full bg-gradient-to-r from-indigo-500 to-purple-500">
            <MdTrendingUp className="h-6 w-6 text-white" />
          </div>
          <h4 className="ml-4 text-xl font-bold text-navy-700 dark:text-white">
            Top Compliant Traders
          </h4>
        </div>
        <CardMenu />
      </div>

      {/* Description */}
      <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
        Traders with highest compliance rates 
      </p>

      {/* Task Content */}
      <div className="h-full w-full mt-4">
        {topCompliance.length === 0 ? (
          <div className="text-center py-8">
            <MdPeople className="h-12 w-12 text-gray-300 mx-auto mb-2" />
            <p className="text-gray-500 text-sm">No compliance data available</p>
          </div>
        ) : (
          topCompliance.map((item, index) => (
            <div 
              key={item.id || index} 
              className="mt-3 flex items-center justify-between p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-white/5 transition-all duration-200 border border-transparent hover:border-gray-200"
            >
              <div className="flex items-center justify-center gap-3 flex-1">
                {/* Rank Icon */}
                <div className="flex-shrink-0">
                  {getRankIcon(index)}
                </div>
                
                {/* Rank Number */}
                <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${getRankBadge(index)}`}>
                  #{index + 1}
                </div>
                
                {/* Trader Info */}
                <div className="flex-1">
                 <div className="flex items-center gap-2">
    <p className="text-base font-semibold text-navy-700 dark:text-white">
        {item.description 
            ? item.description.split(' ').slice(0, 5).join(' ') + (item.description.split(' ').length > 20 ? '...' : '')
            : `Trader ${index + 1}`
        }
    </p>
</div>
                 
                </div>
                
                {/* Compliance Rate */}
                <div className="flex-shrink-0 text-right">
                  <div className={`text-lg font-bold ${getComplianceColor(item.tin|| 0)}`}>
                   
                  </div>
              
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Footer */}
      {topCompliance.length > 0 && (
        <div className="mt-4 pt-3 border-t border-gray-200 dark:border-gray-700">
          <p className="text-xs text-center text-gray-500">
            Based on inspection history and compliance records
          </p>
        </div>
      )}
    </Card>
  );
};

export default TaskCard;